import {
  EntityIcon,
  type EntityIconSelector,
  getEntityIconType,
} from '@core/component/EntityIcon';
import { TruncatedText } from '@core/component/FileList/TruncatedText';
import { UserIcon } from '@core/component/UserIcon';
import type { EntityDragData } from '@entity';
import {
  DragDropProvider,
  DragDropSensors,
  DragOverlay,
  useDragDropContext,
} from '@thisbeyond/solid-dnd';
import { Layer } from '@ui';
import {
  type Accessor,
  createContext,
  createMemo,
  createSignal,
  type JSXElement,
  onCleanup,
  Show,
  useContext,
} from 'solid-js';
import { createPointerCollisionDetector } from './pointer-collision';

type DragOperationContextValue = {
  isAltKey: Accessor<boolean>;
};

const DragOperationContext = createContext<DragOperationContextValue>();

export function useDragOperation() {
  const context = useContext(DragOperationContext);
  if (!context) {
    throw new Error('useDragOperation must be used within ItemDndProvider');
  }
  return context;
}

function ItemDragOverlay() {
  const [state] = useDragDropContext() ?? [];
  const activeDraggable = createMemo(() => {
    return state?.active.draggable;
  });

  const iconType = createMemo((): EntityIconSelector => {
    const data = activeDraggable()?.data;
    if (!data) return 'default';
    // Favorite sortables carry a precomputed icon type (see FavoriteDragData
    // in app-sidebar/favorites-section) instead of an entity shape.
    if (data.dragType === 'favorite' || data.dragType === 'channel-label') {
      return data.iconType as EntityIconSelector;
    }
    if (data.dragType === 'stage') return 'default';
    return getEntityIconType(data as EntityDragData);
  });

  // DM channel favorites show the other participant's avatar instead of the
  // entity icon, matching their sidebar row (see FavoriteIcon).
  const dmRecipientId = createMemo((): string | undefined => {
    const data = activeDraggable()?.data;
    if (data?.dragType !== 'favorite') return undefined;
    return data.dmRecipientId as string | undefined;
  });

  // Deal stage rows in CRM settings (see StageDragData in settings/Crm)
  // carry no entity; their chip is the stage dot plus the label.
  const isStage = createMemo(
    () => activeDraggable()?.data.dragType === 'stage'
  );

  const centeredOnPointerStyle = createMemo(() => {
    const overlay = state?.active.overlay;
    const sensor = state?.active.sensor;
    if (!overlay || !sensor) return;

    return {
      transform: `translate(${sensor.coordinates.origin.x - overlay.layout.left}px, ${sensor.coordinates.origin.y - overlay.layout.top}px) translate(-50%, -50%)`,
    };
  });

  return (
    <Layer depth={2}>
      <div
        class="w-auto max-w-75 flex flex-col gap-2 bg-surface p-2 rounded-lg z-drag shadow-md shadow-drop-shadow pointer-events-none"
        style={centeredOnPointerStyle()}
      >
        <div class="flex flex-row items-center gap-2">
          <Show
            when={!isStage()}
            fallback={
              <span class="size-2 shrink-0 rounded-full bg-accent/70" />
            }
          >
            <Show
              when={dmRecipientId()}
              fallback={<EntityIcon size="xs" targetType={iconType()} />}
            >
              {(recipientId) => (
                <UserIcon
                  id={recipientId()}
                  size="sm"
                  suppressClick
                  showTooltip={false}
                />
              )}
            </Show>
          </Show>
          <TruncatedText size="xs">
            {activeDraggable()?.data.name}
          </TruncatedText>
        </div>
        {/* TODO: when multiselect exists */}
        {/* <Show when={activeDraggable()?.data.selectedItems.length > 1}>
        <div class={`${TEXT_SIZE_CLASSES[size ?? 'sm']} text-ink-muted pl-2`}>
          + {activeDraggable()?.data.selectedItems.length - 1} items
        </div>
      </Show> */}
      </div>
    </Layer>
  );
}

export function ItemDndProvider(props: { children: JSXElement }) {
  let pointerPosition: { x: number; y: number } | undefined;
  const pointerWithin = createPointerCollisionDetector(() => pointerPosition);
  const [isAltPressed, setIsAltPressed] = createSignal(false);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.altKey && !isAltPressed()) {
      setIsAltPressed(true);
    }
  };

  const handleKeyUp = (e: KeyboardEvent) => {
    if (!e.altKey && isAltPressed()) {
      setIsAltPressed(false);
    }
  };

  const handlePointerMove = (e: PointerEvent) => {
    pointerPosition = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: MouseEvent) => {
    pointerPosition = { x: e.clientX, y: e.clientY };
  };

  const handlePointerEnd = () => {
    queueMicrotask(() => {
      pointerPosition = undefined;
    });
  };

  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('keyup', handleKeyUp);
  window.addEventListener('pointermove', handlePointerMove, { capture: true });
  window.addEventListener('pointerup', handlePointerEnd, { capture: true });
  window.addEventListener('pointercancel', handlePointerEnd, { capture: true });
  window.addEventListener('mousemove', handleMouseMove, { capture: true });
  window.addEventListener('mouseup', handlePointerEnd, { capture: true });

  onCleanup(() => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
    window.removeEventListener('pointermove', handlePointerMove, {
      capture: true,
    });
    window.removeEventListener('pointerup', handlePointerEnd, {
      capture: true,
    });
    window.removeEventListener('pointercancel', handlePointerEnd, {
      capture: true,
    });
    window.removeEventListener('mousemove', handleMouseMove, {
      capture: true,
    });
    window.removeEventListener('mouseup', handlePointerEnd, {
      capture: true,
    });
  });

  return (
    <DragOperationContext.Provider value={{ isAltKey: isAltPressed }}>
      <DragDropProvider collisionDetector={pointerWithin}>
        <DragDropSensors />
        {props.children}
        <DragOverlay class="z-drag pointer-events-none">
          <ItemDragOverlay />
        </DragOverlay>
      </DragDropProvider>
    </DragOperationContext.Provider>
  );
}
